import { useCallback, useEffect, useMemo, useState } from 'react';
import { BellRing, RefreshCcw, Send, UserRound, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { listCustomers } from '../api/customers';
import { listAdminNotifications, sendAdminNotification } from '../api/notifications';
import { getErrorMessage } from '../api/response';
import Button from '../components/Button';
import FormField, { inputClassName } from '../components/FormField';
import PageHeader from '../components/PageHeader';
import { LoadingState } from '../components/PageState';
import StatCard from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import { dateTime } from '../utils/format';

const emptyForm = {
  target: 'all',
  customer_id: '',
  title: '',
  message: '',
  tipo: 'notificacion_importante'
};

const statusColor = (status) => {
  if (status === 'enviado') return 'green';
  if (status === 'error') return 'red';
  return 'slate';
};

const parseMetadata = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return {};
  }
};

const PushClientes = () => {
  const [history, setHistory] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [historyData, customerData] = await Promise.all([
        listAdminNotifications({ limit: 100 }),
        listCustomers({ limit: 100 })
      ]);
      setHistory(historyData);
      setCustomers(customerData);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cargar el centro push'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stats = useMemo(() => {
    const sent = history.filter((item) => item.estado === 'enviado').length;
    const errors = history.filter((item) => item.estado === 'error').length;
    const uniqueCustomers = new Set(
      history.map((item) => item.customer_id).filter(Boolean)
    ).size;

    return { sent, errors, uniqueCustomers };
  }, [history]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (form.target === 'customer' && !form.customer_id) {
      toast.error('Selecciona un cliente');
      return;
    }

    try {
      setSending(true);
      const result = await sendAdminNotification({
        target: form.target,
        customer_id: form.target === 'customer' ? form.customer_id : undefined,
        title: form.title.trim(),
        message: form.message.trim(),
        tipo: form.tipo,
        data: {
          tipo: form.tipo,
          source: 'admin'
        }
      });

      toast.success(`Push enviado: ${result?.sent || 0} OK, ${result?.errors || 0} error(es)`);
      setForm(emptyForm);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo enviar el push'));
    } finally {
      setSending(false);
    }
  };

  const columns = [
    {
      key: 'fecha',
      label: 'Fecha',
      render: (row) => dateTime(row.fecha)
    },
    {
      key: 'titulo',
      label: 'Mensaje',
      render: (row) => (
        <div className="min-w-[260px]">
          <p className="font-semibold text-gray-900">{row.titulo}</p>
          <p className="line-clamp-2 text-xs text-gray-500">{row.mensaje}</p>
        </div>
      )
    },
    {
      key: 'customer',
      label: 'Cliente',
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900">{row.customer_name || 'Cliente app'}</p>
          <p className="text-xs text-gray-500">{row.customer_email || row.customer_phone || `ID ${row.customer_id || row.user_id}`}</p>
        </div>
      )
    },
    {
      key: 'estado',
      label: 'Estado',
      render: (row) => <StatusBadge label={row.estado} color={statusColor(row.estado)} />
    },
    {
      key: 'provider',
      label: 'Proveedor',
      render: (row) => {
        const metadata = parseMetadata(row.metadata);
        return metadata?.fcm?.provider || metadata?.fcm?.statusCode || '-';
      }
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Push a clientes"
        description="Centro de mensajería push transaccional para clientes con la app instalada."
        actions={<Button icon={RefreshCcw} variant="secondary" onClick={loadData} disabled={loading}>Actualizar</Button>}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard title="Enviados recientes" value={stats.sent} icon={BellRing} color="green" />
        <StatCard title="Clientes alcanzados" value={stats.uniqueCustomers} icon={Users} color="blue" />
        <StatCard title="Errores" value={stats.errors} icon={BellRing} color="red" />
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <form onSubmit={handleSubmit} className="grid gap-4 lg:grid-cols-[240px_1fr_1fr_auto]">
          <FormField label="Destino">
            <select
              className={inputClassName}
              value={form.target}
              onChange={(event) => setForm({ ...form, target: event.target.value, customer_id: '' })}
            >
              <option value="all">Todos los clientes</option>
              <option value="customer">Cliente específico</option>
            </select>
          </FormField>

          {form.target === 'customer' ? (
            <FormField label="Cliente" required>
              <select
                className={inputClassName}
                value={form.customer_id}
                onChange={(event) => setForm({ ...form, customer_id: event.target.value })}
                required
              >
                <option value="">Seleccionar cliente</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.full_name || customer.email || customer.phone || `Cliente ${customer.id}`}
                  </option>
                ))}
              </select>
            </FormField>
          ) : (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
              <div className="flex gap-2">
                <Users className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Se enviará sólo a clientes con token push activo.</span>
              </div>
            </div>
          )}

          <FormField label="Tipo">
            <select
              className={inputClassName}
              value={form.tipo}
              onChange={(event) => setForm({ ...form, tipo: event.target.value })}
            >
              <option value="notificacion_importante">Notificación importante</option>
              <option value="pedido_listo">Pedido listo</option>
              <option value="cobro_pendiente">Cobro pendiente</option>
              <option value="recordatorio_vacuna">Recordatorio vacuna</option>
            </select>
          </FormField>

          <div className="flex items-end">
            <Button type="submit" icon={Send} disabled={sending || !form.title.trim() || !form.message.trim()}>
              {sending ? 'Enviando...' : 'Enviar push'}
            </Button>
          </div>

          <div className="lg:col-span-2">
            <FormField label="Título" required>
              <input
                className={inputClassName}
                value={form.title}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
                maxLength={120}
                required
              />
            </FormField>
          </div>

          <div className="lg:col-span-2">
            <FormField label="Mensaje" required hint="Usar sólo avisos relevantes para la atención, pedido o cuenta del cliente.">
              <textarea
                className={`${inputClassName} min-h-[86px] resize-y`}
                value={form.message}
                onChange={(event) => setForm({ ...form, message: event.target.value })}
                maxLength={500}
                required
              />
            </FormField>
          </div>
        </form>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-950">Historial enviado</h2>
            <p className="text-sm text-gray-500">Últimos envíos push registrados por dispositivo.</p>
          </div>
          <UserRound className="h-5 w-5 text-gray-400" />
        </div>
        {loading ? (
          <div className="p-5"><LoadingState rows={5} /></div>
        ) : (
          <Table columns={columns} data={history} emptyMessage="Aún no hay envíos push registrados" />
        )}
      </section>
    </div>
  );
};

export default PushClientes;
